import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithAuth } from './baseQuery';

export const employeeApi = createApi({
  reducerPath: 'employeeApi',
  baseQuery: createBaseQueryWithAuth(),
  tagTypes: ['Employee', 'Department', 'Designation', 'EmployeeSalarySlip'],
  endpoints: (builder) => ({
    // Employee endpoints
    getEmployees: builder.query({
      query: (params = {}) => ({
        url: '/employees',
        params,
      }),
      providesTags: ['Employee'],
    }),
    getEmployee: builder.query({
      query: (id) => `/employees/${id}`,
      providesTags: (result, error, id) => [{ type: 'Employee', id }],
    }),
    getMyProfile: builder.query({
      query: () => '/employees/profile',
      providesTags: ['Employee'],
    }),
    createEmployee: builder.mutation({
      query: (employeeData) => ({
        url: '/employees',
        method: 'POST',
        body: employeeData,
      }),
      invalidatesTags: ['Employee'],
    }),
    generateEmployeeId: builder.query({
      query: () => '/employees/generate-id',
    }),
    updateEmployee: builder.mutation({
      query: ({ id, ...employeeData }) => ({
        url: `/employees/${id}`,
        method: 'PUT',
        body: employeeData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Employee', id }],
    }),
    deleteEmployee: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Employee'],
    }),
    updateTdsDeclaration: builder.mutation({
      query: ({ id, tdsDeclaration }) => ({
        url: `/employees/${id}/tds-declaration`,
        method: 'PUT',
        body: { tdsDeclaration },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Employee', id }, 'Employee'],
    }),
    resendCredential: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}/resend-credential`,
        method: 'POST',
      }),
    }),
    
    // Department endpoints (use top-level department routes)
    getDepartments: builder.query({
      query: () => '/departments',
      providesTags: ['Department'],
    }),
    createDepartment: builder.mutation({
      query: (departmentData) => ({
        url: '/departments',
        method: 'POST',
        body: departmentData,
      }),
      invalidatesTags: ['Department'],
    }),
    updateDepartment: builder.mutation({
      query: ({ id, ...departmentData }) => ({
        url: `/departments/${id}`,
        method: 'PUT',
        body: departmentData,
      }),
      invalidatesTags: ['Department'],
    }),
    deleteDepartment: builder.mutation({
      query: (id) => ({
        url: `/departments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Department'],
    }),
    
    // Designation endpoints
    // Designation endpoints (use top-level designation routes)
    getDesignations: builder.query({
      query: (departmentId) => ({
        url: '/designations',
        params: departmentId ? { departmentId } : {},
      }),
      providesTags: ['Designation'],
    }),
    createDesignation: builder.mutation({
      query: (designationData) => ({
        url: '/designations',
        method: 'POST',
        body: designationData,
      }),
      invalidatesTags: ['Designation'],
    }),
    updateDesignation: builder.mutation({
      query: ({ id, ...designationData }) => ({
        url: `/designations/${id}`,
        method: 'PUT',
        body: designationData,
      }),
      invalidatesTags: ['Designation'],
    }),
    deleteDesignation: builder.mutation({
      query: (id) => ({
        url: `/designations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Designation'],
    }),
    
    // Employee management actions
    activateEmployee: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}/activate`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Employee', id }],
    }),
    deactivateEmployee: builder.mutation({
      query: (id) => ({
        url: `/employees/${id}/deactivate`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Employee', id }],
    }),
    uploadEmployeeDocument: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/employees/${id}/documents`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Employee', id }],
    }),
    deleteEmployeeDocument: builder.mutation({
      query: ({ id, documentId }) => ({
        url: `/employees/${id}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Employee', id }],
    }),

    // Employee salary slip endpoints
    getMySalarySlips: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
          if (params[key]) searchParams.append(key, params[key]);
        });
        return `/employees/my-salary-slips?${searchParams.toString()}`;
      },
      providesTags: ['EmployeeSalarySlip'],
    }),

    getMySalarySlipById: builder.query({
      query: (payrollId) => `/employees/salary-slip/${payrollId}`,
      providesTags: (result, error, payrollId) => [
        { type: 'EmployeeSalarySlip', id: payrollId }
      ],
    }),

    downloadMySalarySlip: builder.query({
      query: (payrollId) => ({
        url: `/employees/salary-slip/${payrollId}/download`,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob;
        },
      }),
      // This endpoint returns PDF blob
    }),

    // Get users for manager selection
    getUsers: builder.query({
      query: (params = {}) => ({
        url: '/employees/users',
        params,
      }),
      providesTags: ['User'],
    }),
  }),
});

export const {
  useGetEmployeesQuery,
  useGetEmployeeQuery,
  useGetMyProfileQuery,
  useCreateEmployeeMutation,
  useGenerateEmployeeIdQuery,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useUpdateTdsDeclarationMutation,
  useResendCredentialMutation,
  useGetDepartmentsQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useGetDesignationsQuery,
  useCreateDesignationMutation,
  useUpdateDesignationMutation,
  useDeleteDesignationMutation,
  useActivateEmployeeMutation,
  useDeactivateEmployeeMutation,
  useUploadEmployeeDocumentMutation,
  useDeleteEmployeeDocumentMutation,
  useGetMySalarySlipsQuery,
  useGetMySalarySlipByIdQuery,
  useLazyDownloadMySalarySlipQuery,
  useGetUsersQuery,
} = employeeApi;
